import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Loader2, Download, Plus, Search } from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });
const REASONS = ['customer_return', 'damaged_product', 'billing_error', 'pricing_difference', 'short_supply', 'other'];
const STATUS_CLS = { draft: 'bg-slate-100 text-slate-700', approved: 'bg-emerald-100 text-emerald-800', cancelled: 'bg-red-100 text-red-700' };

export default function CreditNotes() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filters, setFilters] = React.useState({ status: '', invoice_number: '', start_date: '', end_date: '' });
  const [showCreate, setShowCreate] = React.useState(false);
  const [invNo, setInvNo] = React.useState('');
  const [invoice, setInvoice] = React.useState(null);
  const [lines, setLines] = React.useState({});
  const [returnToInv, setReturnToInv] = React.useState(true);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    axios.get(API + '/accounts/credit-notes?' + p, auth())
      .then((r) => setRows(r.data.credit_notes))
      .catch((e) => toast.error(e.response?.data?.detail || 'Load failed'))
      .finally(() => setLoading(false));
  }, [filters]);
  React.useEffect(() => { load(); }, [load]);

  const lookup = () => {
    axios.get(API + '/accounts/invoice-lookup/' + invNo.trim(), auth())
      .then((r) => { setInvoice(r.data.invoice); setLines({}); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Invoice not found'));
  };

  const create = () => {
    const items = Object.entries(lines).filter(([, v]) => v.checked && Number(v.qty) > 0)
      .map(([pid, v]) => ({ product_id: pid, return_qty: Number(v.qty), reason: v.reason || 'customer_return' }));
    if (!items.length) return toast.error('Select at least one product with a return qty');
    setBusy(true);
    axios.post(API + '/accounts/credit-notes', { invoice_number: invoice.invoice_number, items, return_to_inventory: returnToInv }, auth())
      .then((r) => { toast.success('Created ' + r.data.credit_note.credit_note_number); setShowCreate(false); setInvoice(null); setInvNo(''); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Create failed'))
      .finally(() => setBusy(false));
  };

  const act = (id, action) => {
    axios.post(API + `/accounts/credit-notes/${id}/${action}`, {}, auth())
      .then(() => { toast.success('Credit note ' + action + 'd'); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Action failed'));
  };

  const exportXlsx = () => {
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    axios.get(API + '/accounts/credit-notes/export.xlsx?' + p, { ...auth(), responseType: 'blob' })
      .then((r) => {
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = Object.assign(document.createElement('a'), { href: url, download: 'credit-notes.xlsx' });
        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      }).catch(() => toast.error('Export failed'));
  };

  return (
    <div className="p-6 space-y-4" data-testid="credit-notes-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Credit Notes</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportXlsx} data-testid="cn-export-btn"><Download className="w-4 h-4 mr-1" />Export</Button>
          <CsvDownloadButton onDownload={exportXlsx} testId="cn-export-csv" size="default" />
          <Button onClick={() => setShowCreate(true)} data-testid="cn-create-btn"><Plus className="w-4 h-4 mr-1" />New Credit Note</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="h-9 rounded-md border px-2 text-sm" data-testid="cn-status-filter">
          <option value="">All statuses</option><option value="draft">Draft</option><option value="approved">Approved</option><option value="cancelled">Cancelled</option>
        </select>
        <Input placeholder="Invoice No" className="h-9 w-40" value={filters.invoice_number} onChange={(e) => setFilters({ ...filters, invoice_number: e.target.value })} data-testid="cn-invoice-filter" />
        <Input type="date" className="h-9 w-36" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} data-testid="cn-start-date" />
        <Input type="date" className="h-9 w-36" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} data-testid="cn-end-date" />
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin" /></div> : (
        <div className="overflow-x-auto border rounded-lg bg-white">
          <table className="w-full text-sm" data-testid="cn-table">
            <thead><tr className="border-b bg-slate-50 text-left">
              <th className="p-2">CN No</th><th className="p-2">Invoice</th><th className="p-2">Customer</th>
              <th className="p-2 text-right">Credit ₹</th><th className="p-2 text-right">GST ₹</th>
              <th className="p-2">Return to Inv</th><th className="p-2">Status</th><th className="p-2">By</th><th className="p-2">Date</th><th className="p-2">Actions</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={10} className="p-8 text-center text-slate-400">No credit notes</td></tr> : rows.map((cn) => (
                <tr key={cn.id} className="border-b hover:bg-slate-50" data-testid={'cn-row-' + cn.credit_note_number}>
                  <td className="p-2 font-mono">{cn.credit_note_number}</td>
                  <td className="p-2 font-mono">{cn.invoice_number}</td>
                  <td className="p-2">{cn.customer_name}</td>
                  <td className="p-2 text-right font-semibold">{cn.total_credit}</td>
                  <td className="p-2 text-right">{cn.total_gst}</td>
                  <td className="p-2">{cn.return_to_inventory ? 'Yes' : 'No'}</td>
                  <td className="p-2"><Badge className={STATUS_CLS[cn.status]}>{cn.status}</Badge></td>
                  <td className="p-2 text-xs">{cn.created_by_name}</td>
                  <td className="p-2 text-xs">{(cn.created_at || '').slice(0, 10)}</td>
                  <td className="p-2">
                    {cn.status === 'draft' && (
                      <div className="flex gap-1">
                        <Button size="sm" className="h-7" onClick={() => act(cn.id, 'approve')} data-testid={'cn-approve-' + cn.credit_note_number}>Approve</Button>
                        <Button size="sm" variant="outline" className="h-7" onClick={() => act(cn.id, 'cancel')} data-testid={'cn-cancel-' + cn.credit_note_number}>Cancel</Button>
                      </div>)}
                  </td>
                </tr>))}
            </tbody>
          </table>
        </div>)}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="cn-create-dialog">
          <DialogHeader><DialogTitle>Create Credit Note</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input placeholder="Invoice Number (e.g. KPI-…)" value={invNo} onChange={(e) => setInvNo(e.target.value)} data-testid="cn-lookup-input" />
            <Button onClick={lookup} data-testid="cn-lookup-btn"><Search className="w-4 h-4 mr-1" />Load</Button>
          </div>
          {invoice && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{invoice.customer_name} · {(invoice.created_at || '').slice(0, 10)} · Total ₹{invoice.total_amount}</p>
              <table className="w-full text-xs border rounded">
                <thead><tr className="bg-slate-50 text-left"><th className="p-2">Sel</th><th className="p-2">Product</th><th className="p-2 text-right">Inv Qty</th><th className="p-2 text-right">Credited</th><th className="p-2">Return Qty</th><th className="p-2">Reason</th></tr></thead>
                <tbody>{invoice.items.map((it) => {
                  const l = lines[it.product_id] || {};
                  const maxQ = it.quantity - (it.already_credited_qty || 0);
                  return (
                    <tr key={it.product_id} className="border-b">
                      <td className="p-2"><input type="checkbox" checked={!!l.checked} onChange={(e) => setLines({ ...lines, [it.product_id]: { ...l, checked: e.target.checked, qty: l.qty || maxQ } })} data-testid={'cn-select-' + it.product_id} /></td>
                      <td className="p-2">{it.product_name}</td>
                      <td className="p-2 text-right">{it.quantity}</td>
                      <td className="p-2 text-right">{it.already_credited_qty || 0}</td>
                      <td className="p-2"><Input type="number" className="h-8 w-24" min="0" max={maxQ} value={l.qty ?? ''} onChange={(e) => { const v = e.target.value; setLines({ ...lines, [it.product_id]: { ...l, qty: v === '' ? '' : Math.min(Number(v), maxQ) } }); }} data-testid={'cn-qty-' + it.product_id} /><p className="text-[10px] text-slate-400">max {maxQ}</p></td>
                      <td className="p-2">
                        <select className="h-8 rounded border px-1" value={l.reason || 'customer_return'} onChange={(e) => setLines({ ...lines, [it.product_id]: { ...l, reason: e.target.value } })} data-testid={'cn-reason-' + it.product_id}>
                          {REASONS.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                        </select>
                      </td>
                    </tr>);
                })}</tbody>
              </table>
              <div className="flex items-center gap-2">
                <Switch checked={returnToInv} onCheckedChange={setReturnToInv} data-testid="cn-return-switch" />
                <span className="text-sm">Return quantity to inventory on approval</span>
              </div>
              <Button onClick={create} disabled={busy} data-testid="cn-submit-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Credit Note (Draft)'}</Button>
            </div>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
